"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Trash2, CircleCheckBig } from "lucide-react";
import type { MutableRefObject } from "react";
import type { TradingChartRef } from "@/components/TradingChart/TradingChart";
import { apiClient } from "@/lib/api-client";
import { getViewModeChrome } from "../view-mode-chrome";

interface DeleteDrawingsSuccessProps {
  args?: any;
  result?: any;
  chartRef?: MutableRefObject<TradingChartRef | null>;
  viewMode?: string;
}

export function DeleteDrawingsSuccess({
  args,
  chartRef,
  viewMode,
}: DeleteDrawingsSuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  const hasDeletedDrawingsRef = useRef(false);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasDeletedDrawingsRef.current) return;

    const drawings: Array<{ type: string; id: string }> = Array.isArray(
      args?.drawing_ids
    )
      ? args.drawing_ids
      : [];
    if (!chartRef?.current || drawings.length === 0) return;

    hasDeletedDrawingsRef.current = true;

    const deleteDrawingsAndCapture = async () => {
      try {
        drawings.forEach((drawing) => {
          // Use the type field to call appropriate removal method
          if (drawing.type === "trendline") {
            chartRef.current?.removeTrendLine(drawing.id);
          } else if (drawing.type === "rectangle") {
            chartRef.current?.removeRectangle(drawing.id);
          } else if (drawing.type === "label") {
            chartRef.current?.removeLabel(drawing.id);
          }
        });

        const screenshot = await chartRef.current?.captureBase64?.("image/png");
        const viewport = chartRef.current?.getViewport?.();
      } catch (error) {
        // Silent error handling
      }
    };

    deleteDrawingsAndCapture();
  }, []);

  const drawings: Array<{ type: string; id: string }> = Array.isArray(
    args?.drawing_ids
  )
    ? args.drawing_ids
    : [];
  const count = drawings.length;

  const title = "Drawings removed";
  const subtitle = `${count} drawing${
    count === 1 ? "" : "s"
  } deleted from chart`;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5">
            <Trash2
                className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`}
                strokeWidth={2}
              />
            <div className="flex flex-col">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {title}
              </span>
              <span className={`text-[11px] ${chrome.subtext} truncate`}>
                {subtitle}
              </span>
            </div>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          >
            <CircleCheckBig className="h-4 w-4 text-green-500" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
